import { Fragment } from 'react'
import { useLocation } from 'react-router-dom'

import {
  Box, Image, NavigationLinkGroup, NestedNavigation, SelectBox,
} from '@domain-group/suit-theme'

import useAgencySelector from '@hooks/useAgencySelector'
import useBrand from '@hooks/useBrand'
import useCurrentUser from '@hooks/useCurrentUser'

import type { AgencyModel } from '@domain-group/realhub-backend-api-types'

import generateNavigationItems from './utils/generateNavigationItems'

const AgencyAdminNavigation = () => {
  const { pathname } = useLocation()

  const agencySelectorPayload = useAgencySelector()
  const {
    callbacks: { selectAgencyId },
    hasMultipleAgencies,
    selectedAgency,
    selectedAgencyId = 'all',
    sortedAgencies,
  } = agencySelectorPayload

  const brandPayload = useBrand({ id: selectedAgency.brand_id }, {
    requestOptions: {
      include_brand_logo: true,
    },
    performHttpRequests: !!selectedAgency.brand_id,
  })

  const { brand } = brandPayload

  const currentUserPayload = useCurrentUser()
  const { permissions } = currentUserPayload

  const agencyAdminPagesPermissions: boolean = permissions?.agency?.admin?.agency_admin_pages?.manage !== undefined
   && permissions?.agency?.admin?.agency_admin_pages?.manage !== null
    ? permissions?.agency?.admin?.agency_admin_pages?.manage
    : true

  const agencyAdminUserPermissions: boolean = permissions?.agency?.admin?.users?.manage

  const navigationItems = generateNavigationItems({
    agencyAdminPagesPermissions,
    agencyAdminUserPermissions,
    hasMultipleAgencies,
    selectedAgencyId: selectedAgencyId!,
  })

  const showAllAgencies = hasMultipleAgencies && pathname.includes('/campaignQuoteTemplates')

  return (
    <NestedNavigation>
      <Box flexDirection="column" flexShrink={0}>
        <Box
          alignItems="center"
          backgroundColor={`#${brand.colour_primary || 'ffffff'}`}
          borderBottom="1px solid"
          borderColor="lineColor"
          height={190}
          justifyContent="center"
          padding="xlarge"
        >
          <Image
            backgroundSize="contain"
            borderRadius={5}
            height="100%"
            url={brand?.logo?.small}
            width="100%"
          />
        </Box>

        {hasMultipleAgencies && (
          <Box alignItems="center" borderBottom="1px solid" borderColor="lineColor" padding="large">
            <SelectBox
              callbacks={{ selectItem: (item: { key: number | string }) => selectAgencyId(item.key) }}
              // @ts-expect-error TS7006
              items={[
                showAllAgencies ? { key: 'all', title: 'All Agencies' } : null,
                ...sortedAgencies.map((agency: AgencyModel) => ({
                  key: agency.id,
                  title: agency.short_name || agency.title,
                })),
              ].filter(Boolean)}
              selectedItemKey={selectedAgencyId}
            />
          </Box>
        )}

        <Box flexDirection="column" padding="large">
          {navigationItems.sections?.map(section => (
            <Fragment key={section.key}>
              {section.title && section.visible && <NestedNavigation.SectionHeader title={section.title} />}

              {section.visible && section.items?.map(item => (
                <NavigationLinkGroup key={item.key} link={item} secondaryNav sidebarMaximized />
              ))}
            </Fragment>
          ))}
        </Box>
      </Box>
    </NestedNavigation>
  )
}

export default AgencyAdminNavigation
